import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate } from '../../shared/api';
import type { User } from '../../types/biApp';

type Locale = 'hr' | 'en';

const BROWN = 'var(--ad-ink)';
const GOLD = 'var(--ad-acc)';

const card: React.CSSProperties = { background: '#fff', borderRadius: 14, padding: 18, boxShadow: '0 2px 10px rgba(107,79,28,0.10)', border: '1px solid #efe6d2' };
const btn = (active: boolean): React.CSSProperties => ({ padding: '8px 16px', borderRadius: 999, border: '1px solid ' + (active ? BROWN : '#e0d6c0'), background: active ? BROWN : '#fff', color: active ? '#fff' : BROWN, cursor: 'pointer', fontWeight: 600, fontSize: '0.86rem' });
const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: BROWN, borderBottom: '2px solid #efe6d2' };
const td: React.CSSProperties = { padding: '8px 10px', fontSize: '0.86rem', borderBottom: '1px solid #f3ecdb' };

type Emp = { id: number; first_name: string | null; last_name: string | null };
type Pin = { employee_id: number; pin_code: string };
type Branch = { id: number; name: string; region: string | null };

export function KioskAdminTab({ token, locale }: { token: string; locale: Locale; user: User }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [emps, setEmps] = useState<Emp[] | null>(null);
    const [pins, setPins] = useState<Record<number, string>>({});
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [editing, setEditing] = useState<Emp | null>(null);

    const loadPins = useCallback(() => {
        api<{ pins: Pin[] }>('/hr/kiosk/pins', token)
            .then((r) => setPins(Object.fromEntries(r.pins.map((p) => [p.employee_id, p.pin_code]))))
            .catch(() => undefined);
    }, [token]);

    useEffect(() => {
        api<{ employees: Emp[] }>('/hr/employees', token).then((r) => setEmps(r.employees)).catch((e) => { setErr(String(e?.message ?? e)); setEmps([]); });
        loadPins();
    }, [token, loadPins]);

    const bulk = async () => {
        setBusy(true); setErr(null);
        try { const r = await mutate<{ created: number }>('/hr/kiosk/pins/bulk', token, 'POST', { force: false }); loadPins(); setErr(t(`Generirano novih PIN-ova: ${r.created}.`, `New PINs: ${r.created}.`)); }
        catch (e) { setErr(String((e as Error)?.message ?? e)); }
        finally { setBusy(false); }
    };
    const regen = async (id: number) => {
        try { await mutate('/hr/kiosk/pins', token, 'POST', { employee_id: id }); loadPins(); }
        catch (e) { setErr(String((e as Error)?.message ?? e)); }
    };

    const name = (e: Emp) => [e.first_name, e.last_name].filter(Boolean).join(' ') || `#${e.id}`;

    return (
        <div style={{ display: 'grid', gap: 16 }}>
            <div style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                    <strong style={{ color: BROWN }}>{t('Evidencija — PIN-ovi i poslovnice', 'Time clock — PINs & branches')}</strong>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <a href="?kiosk=1" target="_blank" rel="noreferrer" style={{ ...btn(false), textDecoration: 'none' }}>🕒 {t('Otvori kiosk', 'Open kiosk')}</a>
                        <button type="button" style={btn(true)} disabled={busy} onClick={bulk}>{busy ? t('Radim…', 'Working…') : t('Generiraj PIN svima', 'Generate PINs for all')}</button>
                    </div>
                </div>
                {err ? <div style={{ marginTop: 10, fontSize: '0.82rem', color: err.startsWith('Generirano') || err.startsWith('New') ? '#3f7a3f' : '#b14040' }}>{err}</div> : null}
                <div className="muted" style={{ fontSize: '0.74rem', marginTop: 8 }}>
                    {t('Kiosk po poslovnici: otvori ', 'Per-branch kiosk: open ')}<code>?kiosk=1&branch=ID</code>{t(' na uređaju u toj poslovnici.', ' on the device at that branch.')}
                </div>
            </div>

            <div style={card}>
                {emps === null ? <div className="muted">{t('Učitavam…', 'Loading…')}</div> : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead><tr>
                                <th style={th}>{t('Djelatnik', 'Employee')}</th>
                                <th style={th}>PIN</th>
                                <th style={th}></th>
                            </tr></thead>
                            <tbody>
                                {emps.map((e) => (
                                    <tr key={e.id}>
                                        <td style={td}>{name(e)}</td>
                                        <td style={{ ...td, fontFamily: 'monospace', fontSize: '1rem', letterSpacing: '0.1em', color: pins[e.id] ? BROWN : '#b9a' }}>{pins[e.id] ?? '—'}</td>
                                        <td style={{ ...td, textAlign: 'right' }}>
                                            <button type="button" style={{ ...btn(false), marginRight: 6 }} onClick={() => regen(e.id)}>{pins[e.id] ? t('Novi PIN', 'New PIN') : t('Generiraj', 'Generate')}</button>
                                            <button type="button" style={btn(editing?.id === e.id)} onClick={() => setEditing(editing?.id === e.id ? null : e)}>{t('Poslovnice', 'Branches')}</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {editing ? <BranchEditor token={token} locale={locale} emp={editing} onClose={() => setEditing(null)} /> : null}
        </div>
    );
}

export function BranchEditor({ token, locale, emp, onClose }: { token: string; locale: Locale; emp: { id: number; first_name: string | null; last_name: string | null }; onClose: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [all, setAll] = useState<Branch[] | null>(null);
    const [sel, setSel] = useState<Set<number>>(new Set());
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api<{ assigned: number[]; all: Branch[] }>(`/hr/employees/${emp.id}/branches`, token)
            .then((r) => { setAll(r.all); setSel(new Set(r.assigned)); })
            .catch(() => setAll([]));
    }, [token, emp.id]);

    const toggle = (id: number) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const save = async () => {
        setBusy(true);
        try { await mutate(`/hr/employees/${emp.id}/branches`, token, 'PUT', { department_ids: Array.from(sel) }); onClose(); }
        catch { /* noop */ }
        finally { setBusy(false); }
    };

    const nm = [emp.first_name, emp.last_name].filter(Boolean).join(' ');
    return (
        <div style={{ ...card, borderColor: GOLD }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <strong style={{ color: BROWN }}>{t('Poslovnice — ', 'Branches — ')}{nm}</strong>
                <div>
                    <button type="button" style={{ ...btn(false), marginRight: 6 }} onClick={onClose}>{t('Odustani', 'Cancel')}</button>
                    <button type="button" style={btn(true)} disabled={busy} onClick={save}>{busy ? t('Spremam…', 'Saving…') : t('Spremi', 'Save')}</button>
                </div>
            </div>
            {all === null ? <div className="muted">{t('Učitavam…', 'Loading…')}</div> : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {all.map((b) => (
                        <button key={b.id} type="button" onClick={() => toggle(b.id)} style={btn(sel.has(b.id))}>
                            {sel.has(b.id) ? '✓ ' : ''}{b.name}
                        </button>
                    ))}
                </div>
            )}
            <div className="muted" style={{ fontSize: '0.74rem', marginTop: 10 }}>{t('Smjene su moguće samo u označenim poslovnicama; pogoni i kiosk zamjenu.', 'Shifts only in selected branches; also drives kiosk replacement.')}</div>
        </div>
    );
}
